import initApi, { invalidateApiCache } from '@api/index';
import moment from 'moment';
import i18n from 'i18n-js';
import { createAction } from '@reduxjs/toolkit';
import { build } from 'search-params';
import DeviceInfo from 'react-native-device-info';
import localforage from 'localforage';

import {
  resetTo,
  getSecureValue,
  setSecureValue,
  removeSecureValue,
  isWeb,
  Alert,
  Feedback,
  IDENTITY_SERVER_DATA,
  navigateTo,
  generateRandomBytes,
  navigationRef,
  trimFields,
} from '../../helpers';
import { initRole } from '@components/frame';
import { serverNoAvailable, validateRequestError } from '@reducers/notification';
import { addLogToHistory } from '@reducers/logs';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import { buildBackendCallSuccessLogTitle, buildBackendCallErrorLogTitle, buildSignalREventLogTitle } from '@reducers/dataDogs/helpers';
import { SLOW_BACKEND_CALL_THRESHOLD_MS } from '@reducers/utils/constants';
import { trackUserLoggedOut, trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { TRACKING_DOMAINS_LIST } from '@services/tracking/config';

import { getConnection, closeConnections, anonymSignalR, initSignalR, registerEventSubscription, registerDocumentChainByFileName } from '@reducers/signalR';
import { msalInstance } from '@config/msalConfig';
import { handleTenantReasonResponse } from '@reducers/helpers/tenantStatus';

import type { AnyAction } from '@reduxjs/toolkit';
import type { RootState, AppThunk } from '@store/types';
import type { Api } from '@api';
import type { TypedApiMethod } from '@api/types';
import type {
  BackendCallProps,
  ApiRequestData,
  ResponseHeaders,
  ApiError,
  AppDispatch,
  SignalRConnectionPool,
  SignalRState,
  ApiMethodSelector,
  EventSubscriptionBaseProps,
  EventCallProps,
  EventCallWithThunkProps,
} from './types';

// Re-export types for convenience
export type {
  BackendCallProps,
  ApiRequestData,
  ResponseHeaders,
  ApiError,
  AppDispatch,
  SignalRConnectionPool,
  SignalRState,
  ApiMethodSelector,
  EventSubscriptionBaseProps,
  EventCallProps,
  EventCallWithThunkProps,
} from './types';

export const clear = createAction('clear');

/**
 * Helper to create type-safe BackendCallProps with inferred types from the method.
 * TParams and TResponse are inferred directly from the API method's phantom types.
 *
 * @example
 * // Types are inferred from api.time.checkTimeInfo
 * const response = await dispatch(backendCall(
 *   createApiCall((api) => api.time.checkTimeInfo, { ignoreDefaultErrorHandler: true })
 * ));
 * // response is typed as CheckTimeInfoResponse | undefined
 */
export function createApiCall<TParams, TResponse>(
  method: (api: Api) => TypedApiMethod<TParams, TResponse>,
  options?: Omit<BackendCallProps<TParams, TResponse>, 'method'>
): BackendCallProps<TParams, TResponse> {
  return {
    method: method as ApiMethodSelector<TParams, TResponse>,
    ...options,
  };
}

let pull: Record<number, BackendCallProps> = {};
if (!isWeb) {
  setSecureValue('tokenUpdating', 'false');
}

const inFlightRequestUrls = new Map<string, string>();

const rememberRequestUrl = (headers: unknown, resource: RequestInfo | URL): void => {
  const traceparent = (headers as Record<string, string> | undefined)?.Traceparent;
  if (!traceparent) return;
  const url = typeof resource === 'string' ? resource : (resource as URL | Request).toString();
  inFlightRequestUrls.set(traceparent, url);
};

// Cache DeviceInfo.getUniqueId() to avoid repeated async calls on every request
let cachedDeviceId: string | null = null;

const { fetch: originalFetch } = window;

// Extend window type for custom fetch implementations
declare global {
  interface Window {
    fetchOriginal: typeof fetch;
    fetchOptimized: typeof fetch;
  }
}

// Save the original wrapper for switching
window.fetchOriginal = window.fetch;
window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
  let [resource, config = {}] = args as [
    RequestInfo | URL,
    RequestInit & { ignoreInterceptor?: boolean; anonym?: boolean; dispatch?: AppDispatch },
  ];

  // Fast bypass for specific cases (no overhead) — also opts out of the tenant-status check by design.
  if (
    (config as { ignoreInterceptor?: boolean })?.ignoreInterceptor ||
    (config?.headers as Record<string, string>)?.ignoreInterceptor ||
    (config?.headers as Record<string, string>)?.['X-SignalR-User-Agent'] ||
    TRACKING_DOMAINS_LIST.some((domain) => (resource as string)?.includes(domain))
  ) {
    return await originalFetch(resource, config);
  }

  if (!config?.headers) {
    config.headers = {};
  }

  const token = getSecureValue('token');

  if (token && !(config as { anonym?: boolean }).anonym) {
    (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  if (!(config.headers as Record<string, string>)['Content-Type']) {
    (config.headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  (config.headers as Record<string, string>)['AppHub-Connection-Id'] = getSecureValue('appHubID') || '';
  (config.headers as Record<string, string>)['OnboardingHub-Connection-Id'] = getSecureValue('signalRAnonymID') || '';

  if (!isWeb) {
    // Cache DeviceInfo.getUniqueId() result to avoid repeated async calls
    if (!cachedDeviceId) {
      cachedDeviceId = await DeviceInfo.getUniqueId();
    }
    (config.headers as Record<string, string>)['X-Device-ID'] = cachedDeviceId;
    (config.headers as Record<string, string>)['X-Device-Type'] = 'App';
  } else {
    const getUniqueId = getSecureValue('getUniqueId');
    if (getUniqueId) {
      (config.headers as Record<string, string>)['X-Device-ID'] = getUniqueId;
      (config.headers as Record<string, string>)['X-Device-Type'] = 'App';
    }
  }

  rememberRequestUrl(config.headers, resource);
  const response = await originalFetch(resource, config);
  await handleTenantReasonResponse(response);
  return response;
};

// OPTIMIZED VERSION - Batch all async operations in parallel
window.fetchOptimized = async (...args: Parameters<typeof fetch>): Promise<Response> => {
  let [resource, config = {}] = args as [
    RequestInfo | URL,
    RequestInit & { ignoreInterceptor?: boolean; anonym?: boolean; dispatch?: AppDispatch },
  ];

  // Skip interceptor for certain requests — also opts out of the tenant-status check by design.
  if (
    (config as { ignoreInterceptor?: boolean })?.ignoreInterceptor ||
    (config?.headers as Record<string, string>)?.ignoreInterceptor ||
    (config?.headers as Record<string, string>)?.['X-SignalR-User-Agent'] ||
    TRACKING_DOMAINS_LIST.some((domain) => (resource as string)?.includes(domain))
  ) {
    return await originalFetch(resource, config);
  }

  if (!config?.headers) {
    config.headers = {};
  }

  // OPTIMIZATION: Get all values synchronously (no longer need Promise.all)
  const token = getSecureValue('token');
  const appHubID = getSecureValue('appHubID');
  const signalRAnonymID = getSecureValue('signalRAnonymID');
  const uniqueId = !isWeb && !cachedDeviceId ? await DeviceInfo.getUniqueId() : cachedDeviceId || getSecureValue('getUniqueId');

  // Cache device ID for future use
  if (!isWeb && !cachedDeviceId && uniqueId) {
    cachedDeviceId = uniqueId;
  }

  // Set all headers
  if (token && !(config as { anonym?: boolean }).anonym) {
    (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  if (!(config.headers as Record<string, string>)['Content-Type']) {
    (config.headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  (config.headers as Record<string, string>)['AppHub-Connection-Id'] = appHubID || '';
  (config.headers as Record<string, string>)['OnboardingHub-Connection-Id'] = signalRAnonymID || '';

  if (uniqueId) {
    (config.headers as Record<string, string>)['X-Device-ID'] = uniqueId;
    (config.headers as Record<string, string>)['X-Device-Type'] = 'App';
  }

  rememberRequestUrl(config.headers, resource);
  const response = await originalFetch(resource, config);
  await handleTenantReasonResponse(response);
  return response;
};

// Helper to switch between fetch implementations
export const useOptimizedFetch = (): void => {
  window.fetch = window.fetchOptimized;
};

export const useOriginalFetch = (): void => {
  window.fetch = window.fetchOriginal;
};

export const delay = (delayInms: number): Promise<number> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(2);
    }, delayInms);
  });
};

let processing = false;

export const backendCall =
  <TParams = unknown, TResponse = unknown>(props: BackendCallProps<TParams, TResponse>, oldId?: number): AppThunk<Promise<TResponse | undefined>> =>
  async (dispatch: AppDispatch): Promise<TResponse | undefined> => {
    if (isWeb) {
      const tokenUpdating = getSecureValue('tokenUpdating');

      if (tokenUpdating === 'true' && !props.anonym) {
        await delay(1500);
        return await dispatch(backendCall(props, oldId));
      }
      return await dispatch(backendCallFN(props, oldId));
    }

    if (processing) {
      await delay(100);
      return await dispatch(backendCall(props, oldId));
    }

    processing = true;
    const id = oldId || Math.round(Math.random() * 100000);
    pull[id] = props as BackendCallProps;

    const api = initApi();

    const expiresIn = getSecureValue('expiresIn');

    if (!expiresIn) {
      processing = false;
      return dispatch(backendCallFN(props, id));
    }

    const needUpdateToken = props.anonym ? false : moment().add(5, 'm').isAfter(expiresIn);
    if (!needUpdateToken) {
      processing = false;
      return dispatch(backendCallFN(props, id));
    }
    const tokenUpdating = getSecureValue('tokenUpdating');
    if (tokenUpdating === 'true') {
      processing = false;
      await delay(1500);
      return await dispatch(backendCall(props, id));
    }

    setSecureValue('tokenUpdating', 'true');

    processing = false;

    const refreshInfo: Record<string, string> = {};

    let formdata: Record<string, string> = {};

    try {
      const refreshToken = getSecureValue('refreshToken');
      formdata = {
        grant_type: 'refresh_token',
        refresh_token: refreshToken || '',
        client_id: IDENTITY_SERVER_DATA.client_id,
        client_secret: IDENTITY_SERVER_DATA.client_secret,
      };

      const apiInstance = initApi();
      refreshInfo.start = moment().format('HH:mm:ss');

      const formBody = build(formdata);

      const ENV = getSecureValue('env');

      await dispatch(sendLog({ title: LOGGER_TITLES.USER_REFRESH_TOKEN, data: { ...formdata, ENV }, status: 'debug' }) as unknown as AnyAction);

      const resultDev = await apiInstance.user.login({ params: formBody });
      const data = await resultDev.json();

      if (!data || data.error || !data.access_token) {
        throw data;
      }

      setSecureValue('token', data.access_token);
      setSecureValue('refreshToken', data.refresh_token);
      const newExpiresIn = moment().add(data.expires_in, 's').format('YYYY-MM-DD HH:mm:ss');
      setSecureValue('expiresIn', newExpiresIn);

      await dispatch(
        sendLog({
          title: LOGGER_TITLES.USER_REFRESH_TOKEN_SUCCESS,
          data: { expires_in: data.expires_in, expiresIn: newExpiresIn },
          status: 'debug',
        }) as unknown as AnyAction
      );

      const acc = getSecureValue('accounts');
      if (acc) {
        const accounts = JSON.parse(acc);
        const newAccounts = accounts.map((a: { refreshToken: string; token?: string; expiresIn?: string }) => {
          if (a.refreshToken === refreshToken) {
            return {
              ...a,
              token: data.access_token,
              expiresIn: newExpiresIn,
              refreshToken: data.refresh_token,
            };
          }
          return a;
        });

        setSecureValue('accounts', JSON.stringify(newAccounts));
      }
      await initRole();
      refreshInfo.end = moment().format('HH:mm:ss');

      setSecureValue('tokenUpdating', 'false');
      return dispatch(backendCallFN(props, id));
    } catch (e) {
      console.log(e, 'Error while refresh token');
      pull = {};
      setSecureValue('tokenUpdating', 'false');
      if ((e as Error).message && (e as Error).message === 'Network request failed') {
        // Alert({ title: `No internet connection for refresh token`, text: JSON.stringify(e) });
      } else {
        await dispatch(sendLog({ title: LOGGER_TITLES.REFRESH_TOKEN_ERROR, data: { formdata, error: e, ENV: getSecureValue('env') }, status: 'error' }) as unknown as AnyAction);

        trackEvent(TRACKING_EVENTS.SESSION_EXPIRED, {});
        await dispatch(unlogin());
        return undefined;
      }
    }
  };

export const restoreCall =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    Object.keys(pull)?.forEach((id) => {
      dispatch(backendCallFN(pull[Number(id)], Number(id)));
    });
  };

export const getToken = (): { token: string | null; tokenUpdating: boolean } => {
  const token = getSecureValue('token');
  return { token, tokenUpdating: false };
};

export const backendCallFN =
  <TParams = unknown, TResponse = unknown>(
    props: BackendCallProps<TParams, TResponse>,
    id?: number,
    refreshInfo: Record<string, string> = {}
  ): AppThunk<Promise<TResponse | undefined>> =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<TResponse | undefined> => {
    const {
      before,
      after,
      saveDuration,
      onSuccess,
      onError,
      method,
      params = {} as TParams,
      signal,
      headers = {},
      succesEventCallback,
      ignoreDefaultErrorHandler = false,
      anonym,
    } = props;

    if (before) {
      dispatch(before());
    }

    const traceId = generateRandomBytes(32);
    const requestId = generateRandomBytes(16);

    const traceparent = `00-${traceId}-${requestId}-00`;

    let trimmedParams: TParams = params;
    let endpoint: string | undefined;

    try {
      const { tokenUpdating } = getToken();

      if (tokenUpdating && !anonym) {
        await delay(500);
        return dispatch(backendCallFN(props, id, refreshInfo));
      }

      if (isWeb && !anonym) {
        // Use the per-tab account ID from sessionStorage to look up the correct MSAL account.
        // we dont rely on getActiveAccount() from MSAL since this is in-memory and can be stale/wrong across tabs,
        // but rather use _sf_active_account_id which we assign to each user
        const tabAccountId = getSecureValue('_sf_active_account_id');
        const account = (tabAccountId && msalInstance.getAccountByHomeId(tabAccountId as string))
          || msalInstance.getActiveAccount();
        if (!account) {
          await dispatch(unlogin());
          return;
        }
        const accessTokenRequest = {
          scopes: ['https://smarfac.onmicrosoft.com/api/access_as_user'],
          account,
        };

        try {
          const tokenData = await msalInstance.acquireTokenSilent(accessTokenRequest);
          setSecureValue('tokenUpdating', 'false');
          setSecureValue('token', tokenData.accessToken);
        } catch (e) {
          console.log('[backendCall] acquireTokenSilent failed:', e?.errorCode || e?.message || e);
          setSecureValue('tokenUpdating', 'false');

          // Only redirect for errors that genuinely require interactive auth.
          // For transient errors (interaction_in_progress, etc.), use existing token.
          const errorCode = (e as { errorCode?: string })?.errorCode;
          if (errorCode === 'interaction_required' || errorCode === 'consent_required' || errorCode === 'login_required') {
            await msalInstance.acquireTokenRedirect(accessTokenRequest);
            return;
          }

          // For other errors, try to proceed with the existing stored token
          const existingToken = getSecureValue('token');
          if (!existingToken) {
            await msalInstance.acquireTokenRedirect(accessTokenRequest);
            return;
          }
          // Continue with existing token — the API call may still succeed
        }
      }

      const ENV = getSecureValue('env');

      const requestHeaders: Record<string, string> = {
        'X-Correlation-Id': traceId,
        Traceparent: traceparent,
        'X-App-Environment': ENV as string,
      };

      const api = initApi();

      // Trim all string fields in params before sending to backend
      trimmedParams = (params ? trimFields(params) : params) as TParams;

      const requestDetails: ApiRequestData<TParams> = {
        headers: requestHeaders,
        params: trimmedParams as TParams,
        anonym,
        dispatch,
      };

      if (trimmedParams) {
        requestDetails.body = JSON.stringify(trimmedParams);
      }
      if (signal) {
        requestDetails.signal = signal;
      }

      const apiCallStart = performance.now();
      const apiMethod = method(api);
      const result = await apiMethod(requestDetails);
      endpoint = result.url;
      inFlightRequestUrls.delete(traceparent);
      const duration = performance.now() - apiCallStart;

      if (saveDuration) {
        dispatch(saveDuration(duration));
      }

      const logData = {
        url: result.url,
        status: result.status,
        userParams: trimmedParams,
        duration,
      };

      
      if (duration >= SLOW_BACKEND_CALL_THRESHOLD_MS) {
        await dispatch(sendLog({
          title: buildBackendCallSuccessLogTitle(traceId, traceparent),
          data: logData,
          status: 'debug',
          id: traceId,
        }) as unknown as AnyAction);
      }
      dispatch(addLogToHistory(JSON.parse(JSON.stringify(logData))));

      if (!result || !(result.status === 200)) {
        throw result;
      }

      const text = await result.text();
      let data: TResponse;

      if (text) {
        const parsed = JSON.parse(text);
        // Handle OData responses that wrap data in { value: [...] }
        const rawData = parsed.value !== undefined ? parsed.value : parsed;

        // Apply mapper if present on the method
        if (apiMethod._mapper) {
          data = apiMethod._mapper(rawData) as TResponse;
        } else {
          data = rawData as TResponse;
        }
      } else {
        data = { status: result.status } as unknown as TResponse;
      }

      const headersResponse: ResponseHeaders = { paginationString: result.headers.get('x-pagination') };
      if (onSuccess) {
        if (
          (data as { status_code?: number }).status_code &&
          ((data as { status_code?: number }).status_code === 401 || (data as { status_code?: number }).status_code === 403)
        ) {
          throw { status: 401 };
        }
        if ((onSuccess as { type?: string }).type) {
          if (succesEventCallback) {
            const result = await dispatch((onSuccess as (data: TResponse, headers: ResponseHeaders) => AnyAction)(data, headersResponse));
            return result as TResponse;
          }
          dispatch((onSuccess as (data: TResponse, headers: ResponseHeaders) => AnyAction)(data, headersResponse));
        } else {
          if (succesEventCallback) {
            const result = await (onSuccess as (data: TResponse, headers: ResponseHeaders) => TResponse)(data, headersResponse);
            return result;
          }
          (onSuccess as (data: TResponse, headers: ResponseHeaders) => void)(data, headersResponse);
        }
      }
      if (id) {
        delete pull[id];
      }

      if (Array.isArray(data)) {
        return data as TResponse;
      }

      return { ...data, refreshInfo, headers: headersResponse } as TResponse;
    } catch (e) {
      console.log(e, (e as { status?: number }).status, (e as { data?: unknown }).data, (e as Error).message, 'e');
      const isAbortError = ['The user aborted a request.', 'signal is aborted without reason'].includes((e as Error).message);
      if (!isAbortError) {
        const errorResponse = e as Partial<Response> | undefined;
        const errorMessage = (e as Error)?.message;
        const errorName = (e as Error)?.name;
        await dispatch(sendLog({
          title: buildBackendCallErrorLogTitle(traceId, traceparent),
          data: {
            e,
            endpoint: endpoint || errorResponse?.url || inFlightRequestUrls.get(traceparent),
            httpStatus: errorResponse?.status,
            errorName,
            errorMessage,
            userParams: trimmedParams,
          },
          status: 'error',
          id: traceId,
        }) as unknown as AnyAction);
      }
      inFlightRequestUrls.delete(traceparent);

      if (!ignoreDefaultErrorHandler && e && ((e as { status?: number }).status === 500 || ((e as { status?: number }).status || 0) > 500)) {
        Alert({
          title: i18n.t('The Smart Factory Cloud is unable to process your request at this time'),
          text: i18n.t('Please try again later'),
          position: 'vertical',
          actions: [
            {
              text: i18n.t('Ignore'),
              onPress: () => {},
            },
            {
              style: 'primary',
              text: i18n.t('Report to Smart Factory'),
              onPress: () => {
                Feedback({ type: 'error' });
              },
            },
          ],
        });
        dispatch(serverNoAvailable());
      }

      // 403-only by gateway contract; mobile 403s without a tenant reason fall through (web-only auth-expiry below).
      if (!ignoreDefaultErrorHandler && (e as { status?: number })?.status === 403) {
        if (await handleTenantReasonResponse(e as Response)) {
          pull = {};
          return undefined;
        }
      }

      if (!ignoreDefaultErrorHandler && e && ((e as { status?: number }).status === 401 || (isWeb && (e as { status?: number }).status === 403))) {

        trackEvent(TRACKING_EVENTS.SESSION_EXPIRED, {});

        const route = navigationRef?.current?.getCurrentRoute();
        setSecureValue('lastURL', JSON.stringify(route));
        pull = {};
        await dispatch(unlogin({ keepLastURL: true }));
        return undefined;
      }

      try {
        const error: ApiError = await (e as Response).json();
        error.code = (e as { status?: number }).status;
        if (onError) {
          if ((onError as { type?: string }).type) {
            return (await dispatch((onError as (error: ApiError) => AnyAction)(error))) as unknown as TResponse;
          } else {
            return (onError as (error: ApiError) => TResponse)(error);
          }
        }
      } catch (er) {
        if (ignoreDefaultErrorHandler) {
          if (onError) {
            if ((onError as { type?: string }).type) {
              return (await dispatch((onError as () => AnyAction)())) as unknown as TResponse;
            } else {
              return (onError as () => TResponse)();
            }
          }
        }
        if (!isAbortError) {
          console.log('validateRequestError', e);
          dispatch(validateRequestError());
        }
        return;
      }
    } finally {
      if (after) {
        if ((after as { type?: string }).type) {
          dispatch((after as () => AnyAction)());
        } else {
          (after as () => void)();
        }
      }
    }
  };

export const errorHandl = (error: { responseException?: { exceptionMessage?: string } }): void => {
  Alert({ title: 'Error', text: error?.responseException?.exceptionMessage });
};

let wasUnlogin = false;

export const toLogin = async (): Promise<void> => {
  navigateTo('Login');
};

export const unlogin =
  (params?: { keepLastURL?: boolean; clearAll?: boolean }) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    // Track logout event before clearing user data
    const {
      user: { userInfo },
    } = getState();
    trackUserLoggedOut(userInfo);

    const { keepLastURL, clearAll } = params || {};

    // Capture per-tab values before clearing — these live in sessionStorage
    // and are wiped by removeSecureValue below
    const currentEmail = isWeb ? getSecureValue('email') : null;
    const currentAccountId = isWeb ? getSecureValue('_sf_active_account_id') : null;

    try {
      pull = {};

      removeSecureValue('deviceCode');
      removeSecureValue('token');
      removeSecureValue('expiresIn');
      removeSecureValue('refreshToken');
      removeSecureValue('pushToken');
      removeSecureValue('accounts');
      removeSecureValue('status');
      removeSecureValue('featureEnv');
      removeSecureValue('role');
      removeSecureValue('devRoleOverride');
      removeSecureValue('email');
      removeSecureValue('env');
      invalidateApiCache();
      removeSecureValue('searchHistory');
      removeSecureValue('recentlyViewed');
    } catch (e) {}

    try {
      await dispatch(clear());
      await dispatch(closeConnections() as unknown as AnyAction);
      await initRole();
    } catch (e) {}

    try {
      if (!keepLastURL) {
        removeSecureValue('lastURL');
      }
    } catch (e) {}

    if (wasUnlogin) {
      return;
    }

    wasUnlogin = true;

    if (isWeb) {
      try {
        removeSecureValue('_sf_active_account_id');

        if (clearAll) {
          // Remove ALL users' persist data and ALL MSAL account cache entries
          localforage.clear();
          Object.keys(localStorage)
            .filter((k) => k.startsWith('msal.') || k.includes('-login.smarfac.com-'))
            .forEach((k) => localStorage.removeItem(k));
        } else {
          // Use email captured before sessionStorage was cleared
          if (currentEmail) {
            localforage.removeItem(`persist:root:${currentEmail}`);
          } else {
            localforage.clear();
          }
        }

        // logoutRedirect with a specific account removes only that account's
        // cache entries from MSAL's shared localStorage. Other accounts stay
        // intact for other tabs.
        const account = currentAccountId
          ? msalInstance.getAccountByHomeId(currentAccountId as string)
          : msalInstance.getActiveAccount();

        // await (not return) the promise so async rejections — e.g. no_account_error
        // when MSAL has no active account — are caught here and the user falls
        // through to `resetTo('Login')` below instead of being stranded on the
        // current screen. On success the page unloads for the B2C redirect and
        // the code after `return` never runs.
        await msalInstance.logoutRedirect({ account: account || undefined });
        return;
      } catch (e) {}
    }

    setTimeout(() => {
      wasUnlogin = false;
    }, 5000);

    resetTo('Login', { inviteToken: '', token: '', email: '' });
  };

const createEventSubscription =
  (props: EventSubscriptionBaseProps) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<unknown> => {
    const { eventName, connectionName, eventNameError, timeout = 30, matchEventFunction, execute, onExecuteResult } = props;

    const logicFunction = async (resolve: (value: unknown) => void, reject: (reason: string) => void): Promise<void> => {
      const connection = (getConnection() as SignalRConnectionPool)[connectionName];

      if (!eventName || !execute || !dispatch) {
        return reject('error');
      }

      let timeoutId: ReturnType<typeof setTimeout>;

      const successCallback = (e: unknown): void => {
        dispatch(
          sendLog({
            title: buildSignalREventLogTitle(connectionName, eventName),
            data: { eventName, hub: connectionName, data: e },
            status: 'debug',
          }) as unknown as AnyAction
        );
        console.log(eventName, e, (e as { documentId?: string }).documentId);
        if (matchEventFunction) {
          if (!matchEventFunction(e)) {
            return;
          }
        }

        clearTimeout(timeoutId);
        connection.off(eventName, successCallback);
        if (eventNameError) {
          connection.off(eventNameError, errorCallback);
        }

        return resolve(e);
      };

      const errorCallback = (e: unknown): void => {
        dispatch(
          sendLog({
            title: buildSignalREventLogTitle(connectionName, eventName),
            data: { eventName, hub: connectionName, data: e },
            status: 'warn',
          }) as unknown as AnyAction
        );
        console.log(eventNameError, e);
        clearTimeout(timeoutId);
        connection.off(eventNameError!, errorCallback);
        connection.off(eventName, successCallback);
        return reject(e as string);
      };

      connection.on(eventName, successCallback);

      if (eventNameError) {
        connection.on(eventNameError, errorCallback);
      }

      const res = await execute(dispatch);

      if (onExecuteResult && typeof onExecuteResult === 'function') {
        onExecuteResult(res);
      }

      timeoutId = setTimeout(() => {
        connection.off(eventName, successCallback);
        if (eventNameError) {
          connection.off(eventNameError, errorCallback);
        }
        return reject('timeout');
      }, timeout * 1000);
    };

    const checkIfConnected = async (resolve: (value: unknown) => void, reject: (reason: string) => void): Promise<void> => {
      const connection = (getConnection() as SignalRConnectionPool)[connectionName];
      if (!connection) {
        const { signalR } = getState();
        const signalRState = signalR as unknown as SignalRState;
        const isReconnecting = connectionName === 'appHub' ? signalRState.appHubReconnecting : signalRState.signalRAnonymReconnecting;
        const isInit = connectionName === 'appHub' ? signalRState.appHubInit : signalRState.signalRAnonymInit;
        if (!isReconnecting && !isInit) {
          if (connectionName === 'signalRAnonym') {
            dispatch(anonymSignalR() as unknown as AnyAction);
          } else {
            dispatch(initSignalR() as unknown as AnyAction);
          }
        }
        return setTimeout(() => {
          checkIfConnected(resolve, reject);
        }, 1000) as unknown as void;
      }
      await logicFunction(resolve, reject);
    };

    return new Promise(async (resolve, reject) => {
      await checkIfConnected(resolve, reject);
    });
  };

export const eventCall = <TParams, TResponse>(props: EventCallProps<TParams, TResponse>) =>
  createEventSubscription({
    eventName: props.eventName,
    connectionName: props.connectionName,
    eventNameError: props.eventNameError,
    timeout: props.timeout,
    matchEventFunction: props.matchEventFunction,
    execute: (dispatch) => dispatch(backendCall(props.data)),
    onExecuteResult: props.backendCallHandler,
  });

export const eventCallWithThunk = (props: EventCallWithThunkProps) =>
  createEventSubscription({
    eventName: props.eventName,
    connectionName: props.connectionName,
    eventNameError: props.eventNameError,
    timeout: props.timeout,
    matchEventFunction: props.matchEventFunction,
    execute: (dispatch) => dispatch(props.thunk),
    onExecuteResult: props.thunkHandler,
  });

export const eventInvoke = async ({ eventName, connectionName }: { eventName: string; connectionName: string }): Promise<void> => {
  const logicFunction = async (): Promise<void> => {
    const connection = (getConnection() as SignalRConnectionPool)[connectionName];
    if (!eventName || !connection) {
      return;
    }
    const deviceId = await DeviceInfo.getUniqueId();
    connection.invoke(eventName, deviceId);
  };

  const checkIfConnected = async (): Promise<void> => {
    const connection = (getConnection() as SignalRConnectionPool)[connectionName];
    if (!connection) {
      return setTimeout(() => {
        checkIfConnected();
      }, 1000) as unknown as void;
    }
    await logicFunction();
  };

  await checkIfConnected();
};

interface CheckEventOptions {
  timeout?: number;
  matcher?: ((data: unknown) => boolean) | null;
  connectionName?: string;
  signal?: AbortSignal;
}

/**
 * Wait for a specific SignalR event to be triggered.
 * Pass `signal` to tear down the subscription immediately on abort (e.g. component unmount).
 */
export const checkEvent = (eventName: string, options: CheckEventOptions = {}): Promise<unknown> => {
  const { timeout = 30000, matcher = null, connectionName = 'appHub', signal } = options;

  return new Promise((resolve, reject) => {
    if (!eventName) {
      return reject(new Error('Event name is required'));
    }
    if (signal?.aborted) {
      return reject(new DOMException('Aborted', 'AbortError'));
    }

    let cleanup: (() => void) | null = null;
    let onAbort: (() => void) | null = null;

    const waitForConnection = (): void => {
      if (signal?.aborted) return;
      const connections = getConnection() as SignalRConnectionPool;
      const connection = connections[connectionName];

      if (!connection) {
        setTimeout(waitForConnection, 1000);
        return;
      }

      cleanup = registerEventSubscription(
        eventName,
        (eventData: unknown) => {
          resolve(eventData);
        },
        (error: Error) => {
          reject(error);
        },
        matcher,
        timeout,
        connectionName
      );
    };

    waitForConnection();

    const globalTimeout = setTimeout(() => {
      if (cleanup) {
        cleanup();
      }
      reject(new Error(`Global timeout: Event '${eventName}' not received within ${timeout}ms`));
    }, timeout + 1000);

    if (signal) {
      onAbort = () => {
        if (cleanup) cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
      };
      signal.addEventListener('abort', onAbort, { once: true });
    }

    const originalResolve = resolve;
    const originalReject = reject;

    (resolve as (value: unknown) => void) = (...args: [unknown]) => {
      clearTimeout(globalTimeout);
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
      originalResolve(...args);
    };

    (reject as (reason?: unknown) => void) = (...args: [unknown?]) => {
      clearTimeout(globalTimeout);
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
      originalReject(...args);
    };
  });
};

interface CheckEventsOptions extends CheckEventOptions {
  waitForAll?: boolean;
}

interface ReceivedEvent {
  eventName: string;
  data: unknown;
  timestamp: number;
}

/**
 * Wait for multiple SignalR events
 */
export const checkEvents = (eventNames: string[], options: CheckEventsOptions = {}): Promise<unknown> => {
  const { timeout = 30000, matcher = null, connectionName = 'appHub', waitForAll = false } = options;

  return new Promise((resolve, reject) => {
    if (!eventNames || !Array.isArray(eventNames) || eventNames.length === 0) {
      return reject(new Error('Event names array is required'));
    }

    const cleanupFunctions: (() => void)[] = [];
    const receivedEvents = new Map<string, ReceivedEvent>();
    let globalTimeout: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    const cleanup = (): void => {
      if (settled) return;
      settled = true;

      cleanupFunctions?.forEach((fn) => fn());
      cleanupFunctions.length = 0;

      if (globalTimeout) {
        clearTimeout(globalTimeout);
        globalTimeout = null;
      }
    };

    const checkCompletion = (): void => {
      if (waitForAll) {
        if (receivedEvents.size === eventNames.length) {
          cleanup();
          resolve(Array.from(receivedEvents.values()));
        }
      } else {
        if (receivedEvents.size > 0) {
          cleanup();
          const [firstEvent] = receivedEvents.values();
          resolve(firstEvent);
        }
      }
    };

    const waitForConnection = (): void => {
      const connections = getConnection() as SignalRConnectionPool;
      const connection = connections[connectionName];

      if (!connection) {
        setTimeout(waitForConnection, 1000);
        return;
      }

      eventNames?.forEach((eventName) => {
        const cleanupFn = registerEventSubscription(
          eventName,
          (eventData: unknown) => {
            if (!settled) {
              if (matcher && !matcher(eventData)) {
                return;
              }

              receivedEvents.set(eventName, {
                eventName,
                data: eventData,
                timestamp: Date.now(),
              });
              checkCompletion();
            }
          },
          (error: Error) => {
            if (!settled) {
              cleanup();
              reject(error);
            }
          },
          null,
          timeout,
          connectionName
        );

        cleanupFunctions.push(cleanupFn);
      });
    };

    waitForConnection();

    globalTimeout = setTimeout(() => {
      cleanup();

      if (waitForAll) {
        reject(new Error(`Global timeout: Not all events received within ${timeout}ms. Received: ${Array.from(receivedEvents.keys()).join(', ')}`));
      } else {
        reject(new Error(`Global timeout: None of the events [${eventNames.join(', ')}] received within ${timeout}ms`));
      }
    }, timeout + 1000);
  });
};

interface DocumentProcessingChainConfig {
  fileName: string;
  productionOperationId: number;
  timeout?: number;
}

interface DocumentProcessingChainResult {
  startedDocumentProcess: unknown;
  processedDocument: unknown;
  addedProductionInfo: unknown;
  correlationData: {
    documentBlobId: string;
    duration: number;
  };
}

/**
 * Wait for a document processing chain to complete
 */
export const waitForDocumentProcessingChain = (config: DocumentProcessingChainConfig): Promise<DocumentProcessingChainResult> => {
  const { fileName, productionOperationId, timeout = 120000 } = config;

  return new Promise((resolve, reject) => {
    if (!fileName) {
      const error = new Error('fileName is required');
      return reject(error);
    }

    if (!productionOperationId) {
      const error = new Error('productionOperationId is required');
      return reject(error);
    }

    let cleanup: (() => void) | null = null;

    const waitForConnection = (): void => {
      const connections = getConnection() as SignalRConnectionPool;
      const connection = connections['appHub'];

      if (!connection) {
        setTimeout(waitForConnection, 1000);
        return;
      }

      cleanup = registerDocumentChainByFileName(
        fileName,
        productionOperationId,
        (result: DocumentProcessingChainResult) => {
          console.log(`[DocumentChainAPI] Document processing chain completed successfully`, {
            fileName,
            documentBlobId: result.correlationData.documentBlobId,
            productionOperationId,
            duration: result.correlationData.duration,
          });
          resolve(result);
        },
        (error: Error) => {
          console.error(`[DocumentChainAPI] Document processing chain failed`, {
            fileName,
            productionOperationId,
            error: error.message,
          });
          reject(error);
        },
        timeout
      );

      if (!cleanup) {
        const error = new Error('Failed to register document chain - possibly already exists');
        console.error(`[DocumentChainAPI] Registration failed:`, error.message);
        reject(error);
      } else {
        console.log(`[DocumentChainAPI] Document chain registered successfully`);
      }
    };

    waitForConnection();

    const globalTimeout = setTimeout(() => {
      console.log(`[DocumentChainAPI] Global timeout reached for fileName: ${fileName}`);
      if (cleanup) {
        cleanup();
      }
      reject(new Error(`Global timeout: Document processing chain not completed within ${timeout + 1000}ms`));
    }, timeout + 1000);

    const originalResolve = resolve;
    const originalReject = reject;

    (resolve as (value: DocumentProcessingChainResult) => void) = (...args: [DocumentProcessingChainResult]) => {
      clearTimeout(globalTimeout);
      if (cleanup) {
        cleanup();
      }
      originalResolve(...args);
    };

    (reject as (reason?: unknown) => void) = (...args: [unknown?]) => {
      clearTimeout(globalTimeout);
      if (cleanup) {
        cleanup();
      }
      originalReject(...args);
    };
  });
};
